// v15
// Path: frontend/src/components/QuickBooksSync/StandardMappingsModal.jsx
// Inputs: token, onClose, onSaved
// Outputs: React JSX modal for managing multi-set standard field mapping presets across source and target systems
// Summary: Interface for creating, viewing, configuring, renaming, cloning, and deleting standard field mapping presets organized by named sets (SetName) and source/target system pairs with target-specific entity dropdowns, editable preset naming, per-pair default set designation, and active default deletion protection.

import React, { useState, useEffect, useMemo } from 'react';
import { FieldMappingDesigner } from './FieldMappingDesigner';
import { getTargetEntities, getEntityMeta, normalizeEntityName } from './entityCatalogs';


// Auto-injected unbox helper for standardized API responses
const unbox = async (res) => {
    const payload = await res.json();
    if (payload && typeof payload === 'object' && payload.success === true && payload.data !== undefined) {
        return payload.data;
    }
    return payload;
};

export const StandardMappingsModal = ({ token, onClose, onSaved }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [mappings, setMappings] = useState([]);
    const [availableSets, setAvailableSets] = useState([]);
    const [setsMeta, setSetsMeta] = useState([]);
    const [selectedSet, setSelectedSet] = useState('');
    const [sourceSystems, setSourceSystems] = useState(['Passport', 'QuickBooks', 'Xero']);
    const [selectedSystem, setSelectedSystem] = useState('Passport');
    const [targetSystems, setTargetSystems] = useState(['QuickBooks', 'Xero', 'Passport']);
    const [selectedTargetSystem, setSelectedTargetSystem] = useState('QuickBooks');
    const [selectedEntityType, setSelectedEntityType] = useState('Customer');
    const [selectedMapping, setSelectedMapping] = useState(null);
    const [presetName, setPresetName] = useState('');
    const [presetDescription, setPresetDescription] = useState('');
    const [defaultConnPrefix, setDefaultConnPrefix] = useState('');
    const [defaultDbName, setDefaultDbName] = useState('pbsdata00');
    const [message, setMessage] = useState(null);

    // New Set Modal State
    const [showNewSetModal, setShowNewSetModal] = useState(false);
    const [newSetName, setNewSetName] = useState('');
    const [cloneFromSet, setCloneFromSet] = useState('');

    // Rename Set Modal State
    const [showRenameSetModal, setShowRenameSetModal] = useState(false);
    const [renameNewSetName, setRenameNewSetName] = useState('');

    // Derive target-specific entities catalog
    const targetEntities = useMemo(() => {
        return getTargetEntities(selectedTargetSystem);
    }, [selectedTargetSystem]);

    // Keep selectedEntityType in sync with current target system catalog
    useEffect(() => {
        const exists = targetEntities.some(e => e.entity.toLowerCase() === selectedEntityType.toLowerCase());
        if (!exists && targetEntities.length > 0) {
            handleSelectEntity(targetEntities[0].entity);
        }
    }, [selectedTargetSystem, targetEntities]);

    const isCurrentSetDefault = useMemo(() => {
        const meta = setsMeta.find(s => s.setName === selectedSet);
        return !!meta?.isDefault;
    }, [setsMeta, selectedSet]);

    const fetchMappings = async (activeSet = selectedSet, srcSys = selectedSystem, tgtSys = selectedTargetSystem) => {
        setLoading(true);
        try {
            const setParam = activeSet ? `&setName=${encodeURIComponent(activeSet)}` : '';
            const res = await fetch(
                `/api/sync/standard-mappings?sourceSystem=${encodeURIComponent(srcSys)}&targetSystem=${encodeURIComponent(tgtSys)}${setParam}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (res.ok) {
                const data = await unbox(res);
                const fetched = Array.isArray(data.mappings) ? data.mappings : [];
                setMappings(fetched);

                if (Array.isArray(data.setsMeta)) {
                    setSetsMeta(data.setsMeta);
                }
                if (Array.isArray(data.availableSets) && data.availableSets.length > 0) {
                    setAvailableSets(data.availableSets);
                    if (!activeSet || !data.availableSets.includes(activeSet)) {
                        const targetDefault = data.defaultSetName || data.availableSets[0] || '';
                        setSelectedSet(targetDefault);
                        activeSet = targetDefault;
                    }
                } else {
                    setAvailableSets([]);
                    setSetsMeta([]);
                }

                if (Array.isArray(data.availableSourceSystems) && data.availableSourceSystems.length > 0) {
                    setSourceSystems(data.availableSourceSystems);
                }
                if (Array.isArray(data.availableTargetSystems) && data.availableTargetSystems.length > 0) {
                    setTargetSystems(data.availableTargetSystems);
                }
                if (data.defaultSourceConnPrefix) setDefaultConnPrefix(data.defaultSourceConnPrefix);
                if (data.defaultSourceDatabaseName) setDefaultDbName(data.defaultSourceDatabaseName);

                // Match currently selected entity
                const matched = fetched.find(m => {
                    const norm = normalizeEntityName(m.TargetQBEntity || m.EntityType);
                    return norm.toLowerCase() === selectedEntityType.toLowerCase();
                });

                if (matched) {
                    const canonical = normalizeEntityName(matched.TargetQBEntity || matched.EntityType);
                    setSelectedEntityType(canonical);
                    setSelectedMapping(matched);
                    setPresetName(matched.MappingName || `${activeSet} - ${srcSys} ${canonical} -> ${tgtSys}`);
                    setPresetDescription(matched.Description || '');
                } else {
                    const meta = getEntityMeta(selectedEntityType, tgtSys);
                    const defaultName = `${activeSet} - ${srcSys} ${selectedEntityType} -> ${tgtSys}`;
                    setSelectedMapping({
                        MappingID: null,
                        SetName: activeSet,
                        SourceSystem: srcSys,
                        TargetSystem: tgtSys,
                        EntityType: selectedEntityType,
                        TargetQBEntity: selectedEntityType,
                        SourceTable: meta.defaultTable || '',
                        MappingName: defaultName,
                        Description: `Preset field mappings for ${selectedEntityType} in ${activeSet} set`,
                        IsDefault: true,
                        mappings: []
                    });
                    setPresetName(defaultName);
                    setPresetDescription(`Preset field mappings for ${selectedEntityType} in ${activeSet} set`);
                }
            }
        } catch (err) {
            console.error('[StandardMappingsModal] Error fetching mappings:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMappings(selectedSet, selectedSystem, selectedTargetSystem);
    }, [selectedSet, selectedSystem, selectedTargetSystem]);

    const handleSelectEntity = (ent) => {
        const canonical = normalizeEntityName(ent);
        setSelectedEntityType(canonical);
        const matched = mappings.find(m => {
            const mNorm = normalizeEntityName(m.TargetQBEntity || m.EntityType);
            return mNorm.toLowerCase() === canonical.toLowerCase();
        });
        const meta = getEntityMeta(canonical, selectedTargetSystem);
        const defaultName = `${selectedSet} - ${selectedSystem} ${canonical} -> ${selectedTargetSystem}`;
        if (matched) {
            setSelectedMapping(matched);
            setPresetName(matched.MappingName || defaultName);
            setPresetDescription(matched.Description || '');
        } else {
            setSelectedMapping({
                MappingID: null,
                SetName: selectedSet,
                SourceSystem: selectedSystem,
                TargetSystem: selectedTargetSystem,
                EntityType: canonical,
                TargetQBEntity: canonical,
                SourceTable: meta.defaultTable || '',
                MappingName: defaultName,
                Description: `Preset field mappings for ${canonical} in ${selectedSet} set`,
                IsDefault: true,
                mappings: []
            });
            setPresetName(defaultName);
            setPresetDescription(`Preset field mappings for ${canonical} in ${selectedSet} set`);
        }
        setMessage(null);
    };

    const handleCreateNewSet = async (e) => {
        if (e) e.preventDefault();
        const trimmed = newSetName.trim();
        if (!trimmed) {
            useSyncStore.getState().showAlert('Please enter a name for the new standard set.');
            return;
        }
        setSaving(true);
        try {
            const res = await fetch('/api/sync/standard-sets', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    newSetName: trimmed,
                    cloneFromSetName: cloneFromSet === '__NONE__' ? undefined : cloneFromSet,
                    sourceSystem: selectedSystem,
                    targetSystem: selectedTargetSystem
                })
            });
            const data = await unbox(res);
            if (res.ok) {
                setMessage({ type: 'success', text: `Standard Set "${trimmed}" created successfully!` });
                setShowNewSetModal(false);
                setNewSetName('');
                if (!availableSets.includes(trimmed)) {
                    setAvailableSets(prev => [...prev, trimmed]);
                }
                setSelectedSet(trimmed);
                await fetchMappings(trimmed, selectedSystem, selectedTargetSystem);
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to create standard set.' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setSaving(false);
        }
    };

    const handleRenameSet = async (e) => {
        if (e) e.preventDefault();
        const trimmed = renameNewSetName.trim();
        if (!trimmed) {
            useSyncStore.getState().showAlert('Please enter a new name for the standard set.');
            return;
        }
        setSaving(true);
        try {
            const res = await fetch(`/api/sync/standard-sets/${encodeURIComponent(selectedSet)}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ newSetName: trimmed })
            });
            const data = await unbox(res);
            if (res.ok) {
                setMessage({ type: 'success', text: `Standard Set "${selectedSet}" renamed to "${trimmed}"!` });
                setShowRenameSetModal(false);
                setAvailableSets(prev => prev.map(s => s === selectedSet ? trimmed : s));
                setSelectedSet(trimmed);
                await fetchMappings(trimmed, selectedSystem, selectedTargetSystem);
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to rename standard set.' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setSaving(false);
        }
    };

    const handleSetDefault = async () => {
        if (!selectedSet) return;
        setSaving(true);
        try {
            const res = await fetch('/api/sync/standard-sets/set-default', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    setName: selectedSet,
                    sourceSystem: selectedSystem,
                    targetSystem: selectedTargetSystem
                })
            });
            const data = await unbox(res);
            if (res.ok) {
                setMessage({ type: 'success', text: `Standard Set "${selectedSet}" is now the default set for ${selectedSystem} -> ${selectedTargetSystem}!` });
                await fetchMappings(selectedSet, selectedSystem, selectedTargetSystem);
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to designate default set.' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteSet = async () => {
        if (availableSets.length <= 1) {
            useSyncStore.getState().showAlert('Cannot delete the only remaining standard set.');
            return;
        }
        if (isCurrentSetDefault) {
            useSyncStore.getState().showAlert(`Cannot delete Standard Set "${selectedSet}" because it is currently designated as the Default set for ${selectedSystem} -> ${selectedTargetSystem}. Please designate another set as the default first.`);
            return;
        }
        if (!(await useSyncStore.getState().showConfirm(`Are you sure you want to permanently delete the entire Standard Set "${selectedSet}" and all its mapping presets?`))) {
            return;
        }
        setSaving(true);
        try {
            const res = await fetch(`/api/sync/standard-sets/${encodeURIComponent(selectedSet)}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await unbox(res);
            if (res.ok) {
                setMessage({ type: 'success', text: `Standard Set "${selectedSet}" deleted successfully.` });
                const remainingSets = availableSets.filter(s => s !== selectedSet);
                const nextSet = remainingSets[0] || '';
                setAvailableSets(remainingSets);
                setSelectedSet(nextSet);
                await fetchMappings(nextSet, selectedSystem, selectedTargetSystem);
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to delete standard set.' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setSaving(false);
        }
    };

    const handleDeletePreset = async () => {
        if (!selectedMapping?.MappingID) {
            setMessage({ type: 'info', text: 'This preset is not yet saved in the database.' });
            return;
        }
        if (!(await useSyncStore.getState().showConfirm(`Are you sure you want to delete the mapping preset "${presetName || selectedEntityType}" for ${selectedEntityType} in set "${selectedSet}"?`))) {
            return;
        }
        setSaving(true);
        try {
            const res = await fetch(`/api/sync/standard-mappings/${selectedMapping.MappingID}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await unbox(res);
            if (res.ok) {
                setMessage({ type: 'success', text: `Mapping preset for ${selectedEntityType} deleted.` });
                await fetchMappings(selectedSet, selectedSystem, selectedTargetSystem);
                if (onSaved) onSaved();
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to delete mapping preset.' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setSaving(false);
        }
    };

    const handleSavePreset = async (designerPayload) => {
        setSaving(true);
        setMessage(null);
        try {
            const current = selectedMapping || {};
            const effectiveName = (presetName || current.MappingName || `${selectedSet} - ${selectedSystem} ${selectedEntityType} -> ${selectedTargetSystem}`).trim();
            const payload = {
                mappingId: current.MappingID || undefined,
                setName: selectedSet,
                sourceSystem: current.SourceSystem || selectedSystem,
                targetSystem: current.TargetSystem || selectedTargetSystem,
                entityType: selectedEntityType,
                targetQBEntity: selectedEntityType,
                sourceTable: designerPayload.sourceTable || current.SourceTable || '',
                mappingName: effectiveName,
                description: (presetDescription || current.Description || '').trim(),
                isDefault: current.IsDefault !== undefined ? current.IsDefault : true,
                mappings: designerPayload.mappings
            };

            const res = await fetch('/api/sync/standard-mappings', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const data = await unbox(res);
            if (res.ok) {
                setMessage({ type: 'success', text: data.message || 'Standard mapping preset saved!' });
                await fetchMappings(selectedSet, selectedSystem, selectedTargetSystem);
                if (onSaved) onSaved();
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to save standard mapping.' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setSaving(false);
        }
    };

    const handleResetDefaults = async () => {
        if (!selectedMapping?.MappingID) {
            setMessage({ type: 'info', text: 'Preset is already blank for this entity.' });
            return;
        }
        await handleDeletePreset();
    };

    const meta = getEntityMeta(selectedEntityType, selectedTargetSystem);

    const activeMappingProp = useMemo(() => {
        const base = selectedMapping || {};
        return {
            MappingID: base.MappingID || null,
            SetName: base.SetName || selectedSet,
            SourceSystem: base.SourceSystem || selectedSystem,
            TargetSystem: base.TargetSystem || selectedTargetSystem,
            EntityType: base.EntityType || selectedEntityType,
            TargetQBEntity: base.TargetQBEntity || selectedEntityType,
            SourceTable: base.SourceTable || meta.defaultTable || '',
            SourceConnPrefix: defaultConnPrefix,
            SourceDatabaseName: defaultDbName,
            MappingName: presetName || base.MappingName || `${selectedSet} - ${selectedSystem} ${selectedEntityType} -> ${selectedTargetSystem}`,
            Description: presetDescription || base.Description || `Preset mappings for ${selectedEntityType} in ${selectedSet} set`,
            IsDefault: base.IsDefault !== undefined ? base.IsDefault : true,
            FieldMappingsJson: base.FieldMappingsJson || (Array.isArray(base.mappings) ? JSON.stringify(base.mappings) : '[]'),
            mappings: Array.isArray(base.mappings) ? base.mappings : []
        };
    }, [selectedMapping, selectedSet, selectedSystem, selectedTargetSystem, selectedEntityType, meta.defaultTable, defaultConnPrefix, defaultDbName, presetName, presetDescription]);

    // Target entities with preset indicator
    const decoratedTargetEntities = useMemo(() => {
        return targetEntities.map(ent => {
            const hasPreset = mappings.some(m => {
                const norm = normalizeEntityName(m.TargetQBEntity || m.EntityType);
                return norm.toLowerCase() === ent.entity.toLowerCase();
            });
            return {
                ...ent,
                label: `${ent.label || ent.name}${hasPreset ? ' ✓ (Preset Defined)' : ''}`
            };
        });
    }, [targetEntities, mappings]);

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget && onClose) {
            onClose();
        }
    };

    return (
        <div
            onClick={handleBackdropClick}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto"
        >
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-6xl shadow-2xl flex flex-col h-[92vh] overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center h-full text-slate-400 text-xs">
                        <span className="material-symbols-outlined animate-spin text-2xl mr-2 text-indigo-400">sync</span>
                        Loading standard mapping preset...
                    </div>
                ) : (
                    <FieldMappingDesigner
                        key={`${selectedSet}_${selectedSystem}_${selectedTargetSystem}_${selectedEntityType}_${selectedMapping?.UpdatedAt || selectedMapping?.MappingID || 'preset'}`}
                        mode="standard"
                        token={token}
                        standardMapping={activeMappingProp}
                        sourceSystem={selectedSystem}
                        onSave={handleSavePreset}
                        onResetDefaults={handleResetDefaults}
                        onClose={onClose}
                        notification={message}
                        onClearNotification={() => setMessage(null)}
                        setContext={{
                            selectedSet,
                            availableSets,
                            setsMeta,
                            isDefaultSet: isCurrentSetDefault,
                            onSelectSet: setSelectedSet,
                            onSetDefault: handleSetDefault,
                            onNewSet: () => { setNewSetName(''); setCloneFromSet(selectedSet); setShowNewSetModal(true); },
                            onRenameSet: () => { setRenameNewSetName(selectedSet); setShowRenameSetModal(true); },
                            onDeleteSet: handleDeleteSet
                        }}
                        systemContext={{
                            selectedSystem,
                            selectedTargetSystem,
                            sourceSystems,
                            targetSystems,
                            onChangeSourceSystem: setSelectedSystem,
                            onChangeTargetSystem: setSelectedTargetSystem
                        }}
                        entityContext={{
                            selectedEntity: selectedEntityType,
                            targetEntities: decoratedTargetEntities,
                            onSelectEntity: handleSelectEntity,
                            presetName,
                            setPresetName,
                            onDeletePreset: handleDeletePreset,
                            hasSavedPreset: !!selectedMapping?.MappingID
                        }}
                    />
                )}
            </div>

            {/* Create New Standard Set Modal */}
            {showNewSetModal && (
                <div
                    onClick={(e) => { if (e.target === e.currentTarget) setShowNewSetModal(false); }}
                    className="fixed inset-0 z-60 flex items-center justify-center bg-black/80 p-4"
                >
                    <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-md shadow-2xl space-y-4">
                        <div className="flex justify-between items-center border-b border-slate-800 pb-3">
                            <h3 className="text-base font-bold text-white flex items-center gap-2">
                                <span className="material-symbols-outlined text-amber-400">add_circle</span>
                                Create Standard Set
                            </h3>
                            <button
                                type="button"
                                onClick={() => setShowNewSetModal(false)}
                                className="text-slate-400 hover:text-white text-lg"
                            >
                                &times;
                            </button>
                        </div>
                        <form onSubmit={handleCreateNewSet} className="space-y-4">
                            <div>
                                <label className="block text-xs font-medium text-slate-300 mb-1">Set Name *</label>
                                <input
                                    type="text"
                                    value={newSetName}
                                    onChange={e => setNewSetName(e.target.value)}
                                    placeholder="e.g. Retail Standard, Wholesale Profile, Xero Standard"
                                    className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500"
                                    required
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-slate-300 mb-1">Clone from Existing Set</label>
                                <select
                                    value={cloneFromSet}
                                    onChange={e => setCloneFromSet(e.target.value)}
                                    className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500"
                                >
                                    <option value="__NONE__">-- Start Blank (No Clone) --</option>
                                    {availableSets.map(s => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                                <p className="text-[11px] text-slate-500 mt-1">
                                    Optionally copies all entity field mappings from an existing standard set into the new set.
                                </p>
                            </div>
                            <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
                                <button
                                    type="button"
                                    onClick={() => setShowNewSetModal(false)}
                                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-xs"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving || !newSetName.trim()}
                                    className="px-4 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white rounded text-xs font-semibold transition-colors"
                                >
                                    {saving ? 'Creating...' : 'Create Set'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Rename Standard Set Modal */}
            {showRenameSetModal && (
                <div
                    onClick={(e) => { if (e.target === e.currentTarget) setShowRenameSetModal(false); }}
                    className="fixed inset-0 z-60 flex items-center justify-center bg-black/80 p-4"
                >
                    <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-md shadow-2xl space-y-4">
                        <div className="flex justify-between items-center border-b border-slate-800 pb-3">
                            <h3 className="text-base font-bold text-white flex items-center gap-2">
                                <span className="material-symbols-outlined text-amber-400">edit</span>
                                Rename Standard Set
                            </h3>
                            <button
                                type="button"
                                onClick={() => setShowRenameSetModal(false)}
                                className="text-slate-400 hover:text-white text-lg"
                            >
                                &times;
                            </button>
                        </div>
                        <form onSubmit={handleRenameSet} className="space-y-4">
                            <div>
                                <label className="block text-xs font-medium text-slate-300 mb-1">Current Name</label>
                                <div className="text-xs text-amber-300 font-bold px-3 py-2 bg-slate-800/60 rounded border border-slate-700/60">
                                    {selectedSet}
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-slate-300 mb-1">New Set Name *</label>
                                <input
                                    type="text"
                                    value={renameNewSetName}
                                    onChange={e => setRenameNewSetName(e.target.value)}
                                    placeholder="Enter new set name..."
                                    className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500"
                                    required
                                    autoFocus
                                />
                            </div>
                            <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
                                <button
                                    type="button"
                                    onClick={() => setShowRenameSetModal(false)}
                                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-xs"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving || !renameNewSetName.trim() || renameNewSetName.trim() === selectedSet}
                                    className="px-4 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white rounded text-xs font-semibold transition-colors"
                                >
                                    {saving ? 'Renaming...' : 'Rename Set'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StandardMappingsModal;

